import React, { useState } from 'react'
import { MessageSquareText, X } from 'lucide-react'
import { Badge, cn } from '@duck/ui'
import { toast } from 'sonner'
import type { PartnerService } from '@duck/api-definitions'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { DialogContentEditorDialog } from '../Dialogs/DialogContentEditorDialog'
import { dialogSnippet } from '../Dialogs/dialogSnippet'

type Slot = 'challengeDisclaimer' | 'submitDisclaimer'
const SLOTS: Slot[] = ['challengeDisclaimer', 'submitDisclaimer']

interface CohortDialogsCardProps {
  cohort: PartnerService.Cohort
  onSave: (patch: Partial<Record<Slot, string>>) => Promise<void>
}

/** Cohort-level start and submit messages as a one-line strip of chips between the cohort header and the tabs.
 *  A chip opens the editor; set chips preview their content and carry a clear button. */
export const CohortDialogsCard: React.FC<CohortDialogsCardProps> = ({ cohort, onSave }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const [editing, setEditing] = useState<Slot | null>(null)
  const [clearing, setClearing] = useState<Slot | null>(null)
  const values: Record<Slot, string> = {
    challengeDisclaimer: cohort.challengeDisclaimer ?? '',
    submitDisclaimer: cohort.submitDisclaimer ?? '',
  }
  const labels: Record<Slot, string> = {
    challengeDisclaimer: t('partner.startMessageLabel'),
    submitDisclaimer: t('partner.submitMessageLabel'),
  }

  // Errors propagate: the editor path toasts below, DeleteDialog toasts its own on the clear path.
  const save = async (slot: Slot, value: string) => {
    await onSave({ [slot]: value })
    toast.success(value === '' ? t('partner.messageCleared') : t('partner.messageSaved'))
  }

  const saveFromEditor = async (slot: Slot, value: string) => {
    try {
      await save(slot, value)
    } catch (error) {
      toast.error(t('errors.somethingWentWrongWeveBeenNotified'))
      throw error
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="cohort-dialogs-card">
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground me-1">
          <MessageSquareText className="h-4 w-4 text-primary" />
          {tP('cohortMessagesTitle')}
        </span>
        {SLOTS.map((slot) => {
          const value = values[slot]
          const isSet = value !== ''
          return (
            <Badge
              key={slot}
              variant={isSet ? 'default' : 'outline'}
              className={cn('max-w-full gap-1 p-0 overflow-hidden', isSet ? 'bg-primary/10 text-primary border-transparent' : 'text-muted-foreground')}
              data-testid={`cohort-message-${slot}`}
            >
              <button
                type="button"
                onClick={() => setEditing(slot)}
                title={isSet ? dialogSnippet(value, 400) : undefined}
                className={cn('flex items-center gap-1 min-w-0 px-2.5 py-0.5', isSet ? 'hover:bg-primary/10' : 'hover:bg-muted')}
              >
                <span className="shrink-0">{labels[slot]}:</span>
                <span className={cn("truncate font-normal", isSet && "max-w-[100px]")}>
                  {isSet ? dialogSnippet(value) : t('partner.messageNotSet')}
                </span>
              </button>
              {isSet && (
                <button
                  type="button"
                  onClick={() => setClearing(slot)}
                  aria-label={t('partner.clearMessage')}
                  title={t('partner.clearMessage')}
                  className="shrink-0 pe-1.5 ps-0.5 py-0.5 hover:text-error"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          )
        })}
      </div>

      <DialogContentEditorDialog
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null) }}
        title={editing === 'challengeDisclaimer' ? t('partner.startMessageDialogTitle') : t('partner.submitMessageDialogTitle')}
        description={editing === 'challengeDisclaimer' ? tP('startMessageDialogDescription') : tP('submitMessageDialogDescription')}
        initialValue={editing ? values[editing] : ''}
        onSave={(value) => editing ? saveFromEditor(editing, value) : Promise.resolve()}
      />

      <DeleteDialog
        open={clearing !== null}
        onOpenChange={(open) => { if (!open) setClearing(null) }}
        title={t('partner.clearMessageTitle')}
        description={tP('clearMessageDescription')}
        deleteButtonText={t('partner.clearMessage')}
        onDelete={async () => { if (clearing) await save(clearing, '') }}
      />
    </>
  )
}
